import { JourneyCompare, JourneyFilterState, SavedViewState } from '../../../models/journey-explorer/journey-explorer';
import { ExplorerView, FLOW_STEP_OPTIONS, FLOW_WINDOWS, FlowSettings } from './journey-explorer-utils';

export const VIEW_PARAM = 'jv';
const MAX_HISTORY = 50;
const DIRECTIONS = ['after', 'before', 'between'];

export const toViewState = (
	filters: JourneyFilterState,
	flow: FlowSettings,
	view: ExplorerView,
	compare: JourneyCompare | null
): SavedViewState => ({ version: 1, filters, flow, view, compare });

const toBase64Url = (text: string): string =>
	btoa(
		encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)))
	)
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');

const fromBase64Url = (text: string): string => {
	const binary = atob(text.replace(/-/g, '+').replace(/_/g, '/'));
	return decodeURIComponent(
		Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
	);
};

export const encodeViewState = (state: SavedViewState): string => toBase64Url(JSON.stringify(state));

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isRange = (value: unknown): boolean =>
	isObject(value) && typeof value.from === 'string' && typeof value.to === 'string';

const optionalName = (value: unknown): value is string | null => value === null || typeof value === 'string';

export const parseViewState = (value: unknown): SavedViewState | null => {
	if (!isObject(value) || value.version !== 1 || !isObject(value.filters) || !isObject(value.flow)) {
		return null;
	}
	const { filters, flow, view, compare } = value;
	const lists = [
		filters.customer_filters,
		filters.dimension_filters,
		filters.interaction_filters,
		filters.transition_filters,
	];
	if (!isRange(filters.customer_start) || !isRange(filters.interaction_range) || !lists.every(Array.isArray)) {
		return null;
	}
	if (
		!DIRECTIONS.includes(flow.direction as string) ||
		!FLOW_STEP_OPTIONS.includes(flow.steps as number) ||
		!FLOW_WINDOWS.some((option) => option.key === flow.within) ||
		!optionalName(flow.anchor) ||
		!optionalName(flow.end ?? null)
	) {
		return null;
	}
	if (view !== 'map' && view !== 'flow' && view !== 'funnel' && view !== 'steps') {
		return null;
	}
	const validCompare =
		compare === null ||
		(isObject(compare) &&
			typeof compare.column === 'string' &&
			typeof compare.a === 'string' &&
			typeof compare.b === 'string');
	if (!validCompare) {
		return null;
	}
	return {
		...(value as unknown as SavedViewState),
		flow: { ...(flow as unknown as FlowSettings), end: (flow.end as string | null | undefined) ?? null },
	};
};

export const decodeViewState = (text: string): SavedViewState | null => {
	try {
		return parseViewState(JSON.parse(fromBase64Url(text)));
	} catch {
		return null;
	}
};

export const viewStateLink = (state: SavedViewState, href: string): string => {
	const url = new URL(href);
	url.searchParams.set(VIEW_PARAM, encodeViewState(state));
	return url.toString();
};

export interface ViewHistory {
	entries: string[];
	index: number;
}

export const EMPTY_HISTORY: ViewHistory = { entries: [], index: -1 };

export const recordHistory = (history: ViewHistory, entry: string): ViewHistory => {
	if (history.entries[history.index] === entry) {
		return history;
	}
	const entries = [...history.entries.slice(0, history.index + 1), entry].slice(-MAX_HISTORY);
	return { entries, index: entries.length - 1 };
};

export const stepHistory = (history: ViewHistory, delta: number): ViewHistory => {
	const index = history.index + delta;
	return index < 0 || index >= history.entries.length ? history : { ...history, index };
};
